<template>
  <div class="container-custom moo-sheet-page moo-branches-page" :style="styleObject">
    <div class="content-custom">
      <div class="moo-sheet-shell">
        <MooSheetHead
          icon="storefront"
          title="Find branches"
          @close="goBack()"
        />

        <div class="fb-note" v-if="freeText">
          <span class="material-icons-outlined">info</span>
          <span>{{ freeText }}</span>
        </div>

        <SearchInput
          class="fb-search"
          placeholder="Search branch"
          :onChangesearchKeyword="onChangesearchKeyword"
        />

        <div v-if="isLoadDone">
          <div class="fb-list" v-if="branches.length">
            <div class="fb-card" v-for="(branch, index) in branches" :key="index">
              <div class="fb-card__head">
                <img
                  class="fb-card__img"
                  :src="branch.file_url"
                  :alt="branch.branch_name"
                />
                <div class="fb-card__title">
                  <div class="fb-card__name">{{ branch.branch_name }}</div>
                  <div class="fb-card__address" v-if="branch.address">
                    <span class="material-icons-outlined">place</span>
                    <span>{{ branch.address }}</span>
                  </div>
                </div>
              </div>

              <div class="fb-hours" v-if="branch.operation_days_text?.length">
                <div
                  class="fb-hours__row"
                  :class="{ 'fb-hours__row--today': item.selected }"
                  v-for="(item, i) in branch.operation_days_text"
                  :key="i"
                >
                  <span class="fb-hours__day">{{ item.day }}</span>
                  <span class="fb-hours__time">{{ item.time }}</span>
                </div>
              </div>

              <button
                type="button"
                class="fb-order"
                @click="gotoOrderingByBranch(branch)"
              >
                <span class="material-icons-round">restaurant_menu</span>
                Order here
              </button>
            </div>
          </div>

          <div class="fb-empty" v-else>
            <span class="material-icons-outlined">store_mall_directory</span>
            <span>No branches available</span>
          </div>
        </div>
      </div>
    </div>

    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import SearchInput from "@/components/SearchInput.vue";
import MooSheetHead from "@/components/MooSheetHead.vue";
import MemberService from "../services/member.service";

// Typing pause before searching, same as the member site's branch finder.
const SEARCH_DEBOUNCE_MS = 1000;

export default {
  name: "FindStores",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    SearchInput,
    MooSheetHead,
  },
  data() {
    return {
      isLoadDone: false,
      branchCode: "",
      tableCode: "",
      pathType: "",
      branches: [],
      freeText: "",
      searchKeyword: "",
      searchTimerId: null,
      loaderActive: false,
      snackbar: null,
      message: "",
    };
  },
  created() {
    this.pathType = this.$route.params.type;
    this.branchCode = this.$store.state.branchCode;
    this.tableCode = this.$route.params.table_code;
    this.getBranches();
  },
  beforeUnmount() {
    clearTimeout(this.searchTimerId);
  },
  computed: {
    styleObject() {
      return {
        "--color": this.$store.state.mainThemeColorTheme2
          ? this.$store.state.mainThemeColorTheme2
          : this.$store.state.color,
      };
    },
  },
  methods: {
    goBack() {
      this.$router.push({
        name: "Profile",
        params: {
          merchant_code: this.$store.state.merchantCode,
          type: this.pathType,
          branch_code: this.branchCode,
          table_code: this.tableCode,
        },
      });
    },
    onChangesearchKeyword(event) {
      this.searchKeyword = event.target.value;
      clearTimeout(this.searchTimerId);
      this.searchTimerId = setTimeout(() => {
        this.getBranches();
      }, SEARCH_DEBOUNCE_MS);
    },
    getBranches() {
      this.loaderActive = true;
      MemberService.findBranches(this.searchKeyword).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.branches = response.data.data.branches ?? [];
            this.freeText = response.data.data.freeText ?? "";
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.isLoadDone = true;
          this.loaderActive = false;
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error?.response?.data?.result?.message ?? "Cannot load branches";
          this.setTimeOutNotification();
          this.isLoadDone = true;
          this.loaderActive = false;
        }
      );
    },
    gotoOrderingByBranch(branch) {
      if (!branch.branch_code) {
        return;
      }
      // A full navigation, not router.push: branch-scoped state is keyed by
      // merchant+branch in localStorage and App.vue re-validates the pair on
      // load, so switching branch wants a clean entry rather than a route swap
      // inside the current branch's session.
      window.location.href =
        location.protocol +
        "//" +
        location.host +
        "/" +
        this.$store.state.merchantCode +
        "/" +
        branch.branch_code;
    },
    closeSnackbar() {
      this.snackbar = null;
    },
    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>

<style scoped>
/* Shell, tokens and sheet head come from assets/css/moo-sheet.css. */
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.content-custom {
  flex: 1;
  overflow: auto;
}

.fb-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  color: #6b5d55;
  background: var(--sheet-08);
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
.fb-note .material-icons-outlined {
  font-size: 16px;
  color: var(--sheet);
  flex-shrink: 0;
}

/* SearchInput sets --color from the legacy moo_color inline, so pin it to the
   sheet accent the way MyReward.vue does. */
.fb-search {
  --color: var(--sheet) !important;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 12px;
  margin-bottom: 16px !important;
}
/* SearchInput is an underline-style field: input.nosubmit carries a hardcoded
   grey border-bottom and :focus swaps it for a 2px accent one. Inside this
   rounded box that reads as a stray rule across the field, so the box takes
   over the focus affordance and the underline is dropped. */
.fb-search :deep(input.nosubmit) {
  border-bottom: none;
  padding: 11px 12px 11px 40px;
  border-radius: 12px;
}
.fb-search :deep(input[type="search"]:focus) {
  border: none !important;
  border-bottom: none !important;
}
.fb-search:focus-within {
  border-color: var(--sheet);
}

.fb-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.fb-card {
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 16px;
  padding: 14px;
}
.fb-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.fb-card__img {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  object-fit: cover;
  background: var(--sheet-08);
  flex-shrink: 0;
}
.fb-card__title {
  min-width: 0;
}
.fb-card__name {
  font-size: 15px;
  font-weight: 800;
  color: #241a12;
  letter-spacing: -0.01em;
  word-break: break-word;
}
.fb-card__address {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 12px;
  color: #857372;
  margin-top: 4px;
  line-height: 1.45;
}
.fb-card__address .material-icons-outlined {
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 1px;
}

.fb-hours {
  margin-top: 12px;
  border-radius: 12px;
  background: var(--sheet-bg);
  border: 1px solid var(--sheet-border);
  overflow: hidden;
}
.fb-hours__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 12px;
  font-size: 12px;
  color: #857372;
}
.fb-hours__row + .fb-hours__row {
  border-top: 1px solid var(--sheet-border);
}
.fb-hours__row--today {
  color: var(--sheet);
  font-weight: 700;
  background: var(--sheet-08);
}
.fb-hours__day {
  flex-shrink: 0;
}
.fb-hours__time {
  text-align: right;
}

.fb-order {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 44px;
  margin-top: 12px;
  border: none;
  border-radius: 9999px;
  background: var(--sheet);
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
  cursor: pointer;
}
.fb-order:active {
  transform: scale(0.98);
}
.fb-order .material-icons-round {
  font-size: 18px;
}

.fb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 40px 0;
  color: #857372;
  font-size: 13px;
}
.fb-empty .material-icons-outlined {
  font-size: 28px;
  color: var(--sheet-border);
}
</style>
